import type {OutlineEntry} from './index.ts';

/** Inclusive physical pages (one-based), with the corresponding book-page labels. */
export interface OutlinePageRange {
  startPageNumber: number;
  endPageNumber: number;
  startPageLabel: string;
  endPageLabel: string;
  displayText: string;
}

/**
 * Ported from ReadOSReaderPDFOutlineRangeResolver.ranges in ReadOS-iPad.
 * Outline order and level define the end; it is never stored as a second binding.
 * Children stay inside their parent's span. Clicking a node still uses its start.
 */
export function resolveOutlinePageRange(
  entryId: string,
  outline: readonly OutlineEntry[],
  pageCount: number,
  labels: ReadonlyArray<{pageNumber: number; label: string}> = [],
): OutlinePageRange | null {
  if (!Number.isSafeInteger(pageCount) || pageCount < 1) return null;
  const index = outline.findIndex(entry => entry.id === entryId);
  if (index < 0) return null;
  const entry = outline[index];
  const clampPage = (page: number) => Math.min(pageCount, Math.max(1, page));
  const level = Math.max(1, entry.level);
  const boundary = outline.slice(index + 1).find(next => Math.max(1, next.level) <= level);
  if (!Number.isSafeInteger(entry.pageNumber) || !Number.isSafeInteger(entry.level)
      || boundary && !Number.isSafeInteger(boundary.pageNumber)) return null;
  const startPageNumber = clampPage(entry.pageNumber);
  const endPageNumber = Math.max(startPageNumber, boundary ? clampPage(boundary.pageNumber) - 1 : pageCount);
  const startPageLabel = labels.find(item => item.pageNumber === startPageNumber)?.label
    ?? (entry.pageLabel?.trim() || String(startPageNumber));
  const endPageLabel = labels.find(item => item.pageNumber === endPageNumber)?.label
    ?? (endPageNumber === startPageNumber ? startPageLabel : String(endPageNumber));
  return {
    startPageNumber, endPageNumber, startPageLabel, endPageLabel,
    displayText: startPageLabel === endPageLabel ? startPageLabel : `${startPageLabel}–${endPageLabel}`,
  };
}
